import { z } from "zod";
import { IdSchema, IsoTimestampSchema, NonEmptyStringSchema, NonNegativeIntegerSchema, PositiveIntegerSchema, SourceRefSchema, UnknownRecordSchema } from "./source.js";
const NonZeroIntegerSchema = z
    .number()
    .int()
    .refine((value) => value !== 0, {
    message: "quantity delta must not be zero"
});
export const OverrideTargetSelectorSchema = z
    .object({
    id: IdSchema.optional(),
    productPartNo: NonEmptyStringSchema.optional(),
    role: NonEmptyStringSchema.optional(),
    itemKind: NonEmptyStringSchema.optional()
})
    .strict()
    .refine((value) => value.id !== undefined ||
    value.productPartNo !== undefined ||
    value.role !== undefined ||
    value.itemKind !== undefined, {
    message: "override target selector must identify at least one target field"
});
export const OverrideQuantityAdjustmentChangeSchema = z
    .object({
    kind: z.literal("adjust_quantity"),
    targetCollection: z.enum(["controllers", "devices", "infrastructure"]),
    selector: OverrideTargetSelectorSchema,
    delta: NonZeroIntegerSchema
})
    .strict();
export const OverrideProductSubstitutionChangeSchema = z
    .object({
    kind: z.literal("substitute_product"),
    targetCollection: z.enum(["controllers", "devices", "infrastructure"]),
    selector: OverrideTargetSelectorSchema,
    fromProductPartNo: NonEmptyStringSchema.optional(),
    toProductPartNo: NonEmptyStringSchema,
    toScreenKind: z.enum(["ts4", "ts7", "rdu"]).optional()
})
    .strict();
export const OverrideStrategyInputChangeSchema = z.union([
    z
        .object({
        kind: z.literal("set_strategy_input"),
        input: z.literal("ui_screen_kind"),
        value: z.enum(["ts4", "ts7", "rdu"])
    })
        .strict(),
    z
        .object({
        kind: z.literal("set_strategy_input"),
        input: z.literal("force_extra_controller"),
        value: z
            .object({
            productPartNo: NonEmptyStringSchema.optional(),
            quantity: PositiveIntegerSchema.optional()
        })
            .strict()
    })
        .strict()
]);
export const OverrideLegacyOperationChangeSchema = z.union([
    z
        .object({
        operation: z.enum(["add_io_module", "add_relay_module"]),
        productPartNo: NonEmptyStringSchema,
        quantity: PositiveIntegerSchema
    })
        .strict(),
    z
        .object({
        operation: z.enum(["add_screen", "remove_screen"]),
        screenKind: z.enum(["ts4", "ts7", "rdu"]),
        quantity: PositiveIntegerSchema
    })
        .strict()
]);
export const OverrideStructuredChangeSchema = z.union([
    OverrideQuantityAdjustmentChangeSchema,
    OverrideProductSubstitutionChangeSchema,
    OverrideStrategyInputChangeSchema,
    OverrideLegacyOperationChangeSchema
]);
export const OverrideDraftSchema = z
    .object({
    id: IdSchema,
    projectId: IdSchema,
    scope: z
        .object({
        level: z.enum([
            "project",
            "apartment_type",
            "requirement",
            "device",
            "mapping",
            "export"
        ]),
        refId: IdSchema.optional()
    })
        .strict(),
    kind: z.enum([
        "assumption",
        "human_override",
        "ve_option",
        "rfi_resolution",
        "scope_exclusion"
    ]),
    intent: NonEmptyStringSchema,
    structuredChange: OverrideStructuredChangeSchema,
    priority: z.enum(["low", "normal", "high", "blocking"]),
    sourceRefs: z.array(SourceRefSchema).min(1),
    reason: NonEmptyStringSchema,
    status: z.enum(["draft", "ready", "rejected"]),
    createdAt: IsoTimestampSchema,
    createdBy: NonEmptyStringSchema
})
    .strict();
export const OverrideEventSchema = z
    .object({
    id: IdSchema,
    overrideId: IdSchema,
    projectId: IdSchema,
    eventType: z.enum([
        "created",
        "previewed",
        "applied",
        "disabled",
        "superseded",
        "rejected"
    ]),
    actor: NonEmptyStringSchema,
    reason: NonEmptyStringSchema,
    sourceRefs: z.array(SourceRefSchema).min(1),
    createdAt: IsoTimestampSchema
})
    .strict();
export const ImpactPreviewChangeSchema = z
    .object({
    kind: z.enum(["product_quantity", "validation_status", "risk_count"]),
    subjectRef: NonEmptyStringSchema,
    before: z.union([z.string(), z.number(), z.boolean(), z.null()]),
    after: z.union([z.string(), z.number(), z.boolean(), z.null()]),
    delta: z.number().optional(),
    summary: NonEmptyStringSchema
})
    .strict();
export const ImpactPreviewSchema = z
    .object({
    id: IdSchema,
    projectId: IdSchema,
    overrideDraftId: IdSchema,
    affectedRequirementIds: z.array(IdSchema),
    affectedApartmentTypeIds: z.array(IdSchema),
    affectedProductRefs: z.array(NonEmptyStringSchema),
    affectedMappingIds: z.array(IdSchema),
    affectedRiskIds: z.array(IdSchema),
    affectedExportIds: z.array(IdSchema),
    summary: NonEmptyStringSchema,
    validationStatus: z.enum(["pass", "warning", "fail"]),
    changes: z.array(ImpactPreviewChangeSchema).optional(),
    createdAt: IsoTimestampSchema
})
    .strict();
export const ToolCallTraceSchema = z
    .object({
    id: IdSchema,
    toolName: NonEmptyStringSchema,
    requestHash: NonEmptyStringSchema,
    responseHash: NonEmptyStringSchema.optional(),
    input: UnknownRecordSchema,
    output: UnknownRecordSchema.optional(),
    status: z.enum(["queued", "running", "succeeded", "failed"]),
    error: NonEmptyStringSchema.optional(),
    startedAt: IsoTimestampSchema,
    completedAt: IsoTimestampSchema.optional()
})
    .strict();
export const JobStepSchema = z
    .object({
    id: IdSchema,
    jobId: IdSchema,
    name: NonEmptyStringSchema,
    status: z.enum([
        "queued",
        "running",
        "succeeded",
        "failed",
        "skipped",
        "cancelled"
    ]),
    attempt: NonNegativeIntegerSchema,
    leaseOwner: NonEmptyStringSchema.optional(),
    heartbeatAt: IsoTimestampSchema.optional(),
    idempotencyKey: NonEmptyStringSchema,
    inputHash: NonEmptyStringSchema,
    outputHash: NonEmptyStringSchema.optional(),
    toolCalls: z.array(ToolCallTraceSchema),
    error: NonEmptyStringSchema.optional(),
    startedAt: IsoTimestampSchema.optional(),
    completedAt: IsoTimestampSchema.optional()
})
    .strict();
export const AgentJobSchema = z
    .object({
    id: IdSchema,
    orgId: IdSchema,
    projectId: IdSchema.optional(),
    version: NonEmptyStringSchema,
    jobType: z.enum([
        "catalog_import",
        "requirement_extraction",
        "spec_run",
        "validation_run",
        "export_build",
        "agent_tool"
    ]),
    status: z.enum([
        "queued",
        "running",
        "succeeded",
        "failed",
        "cancelled",
        "retrying"
    ]),
    steps: z.array(JobStepSchema),
    input: UnknownRecordSchema,
    output: UnknownRecordSchema.optional(),
    error: NonEmptyStringSchema.optional(),
    idempotencyKey: NonEmptyStringSchema,
    retryCount: NonNegativeIntegerSchema,
    createdAt: IsoTimestampSchema,
    updatedAt: IsoTimestampSchema
})
    .strict();
export const EngineToolRequestSchema = z
    .object({
    id: IdSchema,
    toolName: NonEmptyStringSchema,
    projectId: IdSchema,
    specRunId: IdSchema.optional(),
    input: UnknownRecordSchema,
    idempotencyKey: NonEmptyStringSchema,
    requestedAt: IsoTimestampSchema
})
    .strict();
export const EngineToolResponseSchema = z
    .object({
    requestId: IdSchema,
    status: z.enum(["succeeded", "failed"]),
    output: UnknownRecordSchema.optional(),
    validationErrors: z.array(NonEmptyStringSchema),
    error: NonEmptyStringSchema.optional(),
    respondedAt: IsoTimestampSchema
})
    .strict();
//# sourceMappingURL=jobs.js.map